import React from 'react';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { useDeleteEntityMutation } from '../../api/useDeleteEntityMutation';
import { useRouteParams } from '../../hooks/useRouteParams';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { useFilterableYearOption } from '../../state/selectors';
import { EntityDTO, EntityTypeDTO } from '../../DocumentVault.meta';

interface Props {
  entity: EntityDTO;
  onRequestClose(): void;
}

export const DeleteEntity: React.FC<Props> = ({ entity, onRequestClose }) => {
  const { clientId, path, providerType, filterableByYear } = useRouteParams();

  const { value: year } = useFilterableYearOption();

  const {
    mutateAsync: deleteEntity,
    isLoading: isDeletingEntity,
    error: deleteEntityError,
  } = useDeleteEntityMutation();

  const onDeleteEntity = async () => {
    try {
      await deleteEntity({
        clientId,
        objectPath: entity.path,
        objectProviderType: entity.providerType,
        path,
        providerType,
        currentYear: filterableByYear ? year : null,
      });

      onRequestClose();
    } catch {
      // Ignore
    }
  };

  return (
    <Modal isOpen onRequestClose={onRequestClose} shouldCloseOnOverlayClick={false} size="medium">
      <ModalHeader variant="warning">
        Delete {entity.type === EntityTypeDTO.Folder ? 'Folder' : 'Document'}
      </ModalHeader>
      <ModalCloseButton onClick={onRequestClose} disabled={isDeletingEntity} />

      {deleteEntityError && (
        <Alert message={deleteEntityError.message} variant="error" mx="24" mt="0" mb="16" />
      )}

      <ModalBody>
        You are about to delete an item from the Document Vault. This action cannot be undone.{' '}
        <Text as="span" fontWeight={500}>
          Are you sure you want to delete the{' '}
          {entity.type === EntityTypeDTO.Folder ? 'folder' : 'document'} &quot;{entity.name}&quot;?
        </Text>
      </ModalBody>

      <ModalFooter>
        <Button variant="subtleInactive" onClick={onRequestClose} disabled={isDeletingEntity}>
          Cancel
        </Button>

        <Button variant="error" isLoading={isDeletingEntity} onClick={onDeleteEntity} ml="12">
          Delete
        </Button>
      </ModalFooter>
    </Modal>
  );
};
